'use client';

/**
 * Health tab — the operational surface of the admin dialog (scale program;
 * promoted from an Analytics-tab strip to its own tab 2026-08-05 at V.'s
 * request). ONE place for the decision-driving signals: active alerts, the
 * serving path, fleet shape, the agent runtime, and the collab incident
 * signatures from the 2026-08-05 record. Reads /api/admin/health
 * (staff-gated; queries Prometheus + Alertmanager in-cluster), so the tab
 * and the pager can never disagree. Grafana keeps the deep-dive dashboards;
 * Cloud Monitoring keeps the DB signals (the D1 resize alert lives there).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Activity, AlertTriangle, Boxes, Bot, CheckCircle2, Clock3, Database,
  DoorClosed, HeartPulse, MonitorX, RefreshCw, Server, Users2, Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface HealthResponse {
  at: string;
  signals: {
    errorRate5m: number | null;
    p95Seconds: number | null;
    requestRate: number | null;
    frontendErrorRate: number | null;
    portalWebPods: number | null;
    portalAgentPods: number | null;
    portalJobsPods: number | null;
    collabPods: number | null;
    executorPods: number | null;
    pgbouncerPods: number | null;
    milvusPods: number | null;
    nodesUp: number | null;
    agentSingleflightRate: number | null;
    agentMissRate: number | null;
    executorRequestRate: number | null;
    collabRooms: number | null;
    collabClients: number | null;
    collabConnAgeP95: number | null;
    heartbeatDeadPods: number | null;
    roomFullRate: number | null;
    persistErrorRate: number | null;
    yjsCorruptionRate: number | null;
  };
  alerts: { name: string; severity: string; summary: string; since: string }[] | null;
  monitoringReachable: boolean;
}

const REFRESH_MS = 30_000;

function fmt(v: number | null, digits = 1, suffix = ''): string {
  if (v == null) return 'n/a';
  return `${v.toFixed(digits)}${suffix}`;
}

function fmtInt(v: number | null): string {
  return v == null ? 'n/a' : String(Math.round(v));
}

function Kpi({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  tone?: 'positive' | 'warning';
}) {
  // Same tile idiom as analytics-tab.tsx so the two tabs read as one system.
  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon
          className={cn(
            'h-3 w-3',
            tone === 'positive' && 'text-emerald-500',
            tone === 'warning' && 'text-amber-500',
          )}
        />
        {label}
      </div>
      <div className="text-base font-semibold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">{sub}</div>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{children}</div>
    </section>
  );
}

export function HealthTab() {
  const [data, setData] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(() => {
    setRefreshing(true);
    fetch('/api/admin/health')
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<HealthResponse>;
      })
      .then((json) => {
        setData(json);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setRefreshing(false));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  if (error && !data) {
    return (
      <div className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2">
        Failed to load health signals: {error}
      </div>
    );
  }
  if (!data) return null;

  if (!data.monitoringReachable) {
    return (
      <div className="rounded-xl border border-border p-4 text-sm text-muted-foreground flex items-center gap-2">
        <MonitorX className="h-4 w-4" />
        The monitoring stack is not reachable from this environment. Signals appear when the
        portal can reach Prometheus in the cluster.
      </div>
    );
  }

  const s = data.signals;
  const alerts = data.alerts ?? [];
  const errPct = s.errorRate5m == null ? null : s.errorRate5m * 100;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Platform health</h2>
          <p className="text-xs text-muted-foreground">
            Live from Prometheus and Alertmanager. Thresholds mirror the alert rules, so this
            tab and the pager always agree.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className={cn('h-3 w-3', refreshing && 'animate-spin')} />
          refresh
        </button>
      </header>

      {alerts.length === 0 ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
          No active alerts.
        </div>
      ) : (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 space-y-1.5">
          {alerts.map((a, i) => (
            <div key={`${a.name}-${i}`} className="flex items-start gap-2 text-xs">
              <span
                className={cn(
                  'mt-0.5 inline-block rounded px-1 py-px text-[9px] font-semibold uppercase',
                  a.severity === 'page' || a.severity === 'critical'
                    ? 'bg-red-500/15 text-red-500'
                    : 'bg-amber-500/15 text-amber-600',
                )}
              >
                {a.severity}
              </span>
              <div className="min-w-0">
                <span className="font-medium">{a.name}</span>
                {a.summary && <span className="text-muted-foreground"> · {a.summary}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Section title="Serving path">
        <Kpi
          icon={AlertTriangle}
          label="Error rate 5m"
          value={errPct == null ? 'n/a' : `${errPct.toFixed(2)}%`}
          sub="HTTP 5xx share"
          tone={errPct != null && errPct > 1 ? 'warning' : 'positive'}
        />
        <Kpi
          icon={Clock3}
          label="Latency p95"
          value={fmt(s.p95Seconds, 2, 's')}
          sub={`${fmt(s.requestRate, 0)} requests/s`}
          tone={s.p95Seconds != null && s.p95Seconds > 2 ? 'warning' : undefined}
        />
        <Kpi
          icon={Zap}
          label="Executor"
          value={`${fmt(s.executorRequestRate, 1)}/s`}
          sub="function invocations"
        />
        <Kpi
          icon={MonitorX}
          label="Frontend errors"
          value={`${fmt(s.frontendErrorRate, 2)}/s`}
          sub="reported via /api/errors"
          tone={s.frontendErrorRate != null && s.frontendErrorRate > 0.1 ? 'warning' : undefined}
        />
      </Section>

      <Section title="Fleet">
        <Kpi
          icon={Server}
          label="Portal web"
          value={fmtInt(s.portalWebPods)}
          sub="budget 12 until pgbouncer is live"
          tone={s.portalWebPods != null && s.portalWebPods > 12 && (s.pgbouncerPods ?? 0) === 0 ? 'warning' : undefined}
        />
        <Kpi
          icon={Bot}
          label="Agents / jobs"
          value={`${fmtInt(s.portalAgentPods)} / ${fmtInt(s.portalJobsPods)}`}
          sub="role-split pods (0 before the traffic flip)"
        />
        <Kpi
          icon={Users2}
          label="Collab / executor"
          value={`${fmtInt(s.collabPods)} / ${fmtInt(s.executorPods)}`}
          sub="floors 2 / 1 idle, 4 / 2 launch week"
        />
        <Kpi
          icon={Boxes}
          label="Nodes / pgbouncer / milvus"
          value={`${fmtInt(s.nodesUp)} / ${fmtInt(s.pgbouncerPods)} / ${fmtInt(s.milvusPods)}`}
          sub="auto-provisioning follows the pods"
        />
      </Section>

      <Section title="Agent runtime">
        <Kpi
          icon={Bot}
          label="Agent builds"
          value={`${fmt(s.agentMissRate, 2)}/s`}
          sub="cache misses; about 1 per agent per minute is healthy"
        />
        <Kpi
          icon={Activity}
          label="Stampede absorbed"
          value={`${fmt(s.agentSingleflightRate, 2)}/s`}
          sub="callers joining an in-flight build"
          tone={
            s.agentMissRate != null
            && s.agentSingleflightRate != null
            && s.agentMissRate > s.agentSingleflightRate + 1
              ? 'warning'
              : undefined
          }
        />
      </Section>

      <Section title="Collab">
        <Kpi
          icon={Users2}
          label="Rooms / clients"
          value={`${fmtInt(s.collabRooms)} / ${fmtInt(s.collabClients)}`}
          sub="active rooms / connected clients"
        />
        <Kpi
          icon={Activity}
          label="Socket age p95"
          value={fmt(s.collabConnAgeP95, 0, 's')}
          sub="under 60s means the path kills sockets by age"
          tone={s.collabConnAgeP95 != null && s.collabConnAgeP95 < 60 ? 'warning' : undefined}
        />
        <Kpi
          icon={HeartPulse}
          label="Dead pubsub pods"
          value={fmtInt(s.heartbeatDeadPods)}
          sub="sending heartbeats, receiving none"
          tone={s.heartbeatDeadPods != null && s.heartbeatDeadPods > 0 ? 'warning' : 'positive'}
        />
        <Kpi
          icon={DoorClosed}
          label="Cap rejects / persist / corrupt"
          value={`${fmt(s.roomFullRate, 2)} / ${fmt(s.persistErrorRate, 2)} / ${fmt(s.yjsCorruptionRate, 2)}`}
          sub="per second; all three should be zero"
          tone={
            (s.roomFullRate ?? 0) > 0 || (s.persistErrorRate ?? 0) > 0 || (s.yjsCorruptionRate ?? 0) > 0
              ? 'warning'
              : undefined
          }
        />
      </Section>

      <div className="rounded-xl border border-border p-3 text-[11px] text-muted-foreground flex items-start gap-2">
        <Database className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          Database CPU, connections and replica lag live in GCP Cloud Monitoring, including the
          primary-resize alert that is the one manual scaling decision. Grafana carries the full
          dashboards behind these tiles, including the scale-bottlenecks board.
        </span>
      </div>
    </div>
  );
}
